import { suspendModals } from '@ifixit/ui/lib/modal-stack';

// One rendered widget per area, remembered with the element it was rendered
// into. React unmounts a placeholder whenever the login form changes step,
// and grecaptcha.execute() against a widget whose element left the document
// never calls back, so a new element gets a new widget.
let widgets = {};

/**
 * Loads the recaptcha js API. Safe to call more than once: only the first
 * call adds the script tag.
 */
let recaptchaLoaded = null;
function loadRecaptcha() {
   if (recaptchaLoaded) {
      return recaptchaLoaded;
   }

   return (recaptchaLoaded = new Promise(resolve => {
      window.recaptchaOnload = function () {
         resolve();
      };

      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=recaptchaOnload&render=explicit';
      document.head.append(script);
   }));
}

let recaptchaPromiseResolver;
export const Recaptcha = {
   executeInvisible: function (element, sitekey, area) {
      if (!element) {
         return Promise.resolve();
      }
      if (!sitekey) {
         throw 'sitekey is ' + sitekey;
      }
      if (!area) {
         throw 'area is ' + area;
      }
      return loadRecaptcha().then(
         () =>
            new Promise(resolve => {
               const stopWatching = watchChallenge();
               recaptchaPromiseResolver = token => {
                  stopWatching();
                  resolve(token);
               };
               if (widgets[area]?.element === element) {
                  grecaptcha.reset(widgets[area].id);
               } else {
                  const renderOptions = {
                     sitekey: sitekey,
                     size: 'invisible',
                     // We need to make this dynamic so we don't have a stale
                     // reference to the `resolve` function on the initially
                     // created Promise.
                     callback: token => recaptchaPromiseResolver(token),
                  };
                  widgets[area] = {
                     element,
                     id: grecaptcha.render(element, renderOptions, false),
                  };
               }
               grecaptcha.execute(widgets[area].id);
            })
      );
   },
};

/**
 * When Google decides to ask, it shows its challenge in a frame it keeps at
 * the end of <body>, revealed through its wrapper's inline style. An open
 * modal dialog would leave the challenge under it and ignoring every press,
 * so the dialogs stop being modal while it shows.
 */
let stopWatchingChallenge = () => {};
function watchChallenge() {
   stopWatchingChallenge();
   let resumeModals = null;
   const stop = () => {
      observer.disconnect();
      resumeModals?.();
      resumeModals = null;
   };
   const observer = new MutationObserver(() => {
      const shown = isChallengeShown();
      if (shown && !resumeModals) {
         resumeModals = suspendModals();
      } else if (!shown && resumeModals) {
         // Solved or dismissed.
         stop();
      }
   });
   observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributeFilter: ['style'],
   });
   stopWatchingChallenge = stop;
   return stop;
}

function isChallengeShown() {
   return Array.from(
      document.querySelectorAll('iframe[src*="google.com/recaptcha"][src*="bframe"]')
   ).some(frame => {
      const wrapper = frame.closest('body > div');
      return wrapper instanceof HTMLElement && wrapper.style.visibility === 'visible';
   });
}
